import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, TextField, Divider, InputAdornment } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import NotFound from '../../common/not-found';

import AuthContext from '../../context';
import { get, post } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    divider: {
        marginBottom: 24
    },
    highlightedNumber: {
        color: '#0d6efd'
    },
    controlsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 20
    },
    clearBtn: {
        marginRight: 15,
        backgroundColor: '#A4A4A4',
        borderColor: '#A4A4A4',
        '&:hover': {
            backgroundColor: '#A4A4A4',
            borderColor: '#A4A4A4',
            boxShadow: 'none'
        }
    },
    calculateBtn: {
        marginRight: 15,
        backgroundColor: '#6E6E6E',
        borderColor: '#6E6E6E',
        '&:hover': {
            backgroundColor: '#6E6E6E',
            borderColor: '#6E6E6E',
            boxShadow: 'none'
        }
    },
    storeBtn: {
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none'
        }
    },
}));

const defSupplier = { id: 0, name: 'Choose a supplier' };
const defLocation = { id: 0, name: 'Choose a location' };
const defProduct = { id: 0, name: 'Choose a product' };
const defType = { id: 0, name: 'Choose a type' };

const defTypes = [defType];

const TicketSaleCreate = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const sede = JSON.parse(localStorage.getItem('@sede'));
    const classes = useStyles();
    const history = useHistory();
    const [state, setState] = useState({
        loading: true,
        suppliers: [],
        locations: [],
        products: [],
        types: defTypes,
        oldTicketSale: {},
        ticketSale: {
            idticket_sale: 0,
            code: '',
            date: dayjs().format('YYYY-MM-DD'),
            product_qty: 0,
            qty: 0,
            total_qty: 0,
            price: 0,
            total: 0,
            notes: '',
            supplier: defSupplier,
            location: defLocation,
            product: defProduct,
            type: defType
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Ticket sale - create';

        makeRequest();
    }, []);

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/ticketSale/create');
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                suppliers: response?.suppliers ?? [],
                locations: response?.locations ?? [],
                products: response?.products ?? []
            }));
        } else {
            console.log('[TicketSaleCreate] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, ticketSale: { ...prev.ticketSale, [target.name]: target.value } }));
    const handleAutocomplete = (key, value) => setState(prev => ({ ...prev, ticketSale: { ...prev.ticketSale, [key]: value } }));

    const getTypes = async product => {
        showLoader(true);
        const response = await get(`/product/${product.id}`);
        if (response.status === 200) {
            const { data } = response;
            setState(prev => ({
                ...prev,
                types: [...defTypes, ...data.types],
                ticketSale: { ...prev.ticketSale, type: defType }
            }));
        } else {
            setState(prev => ({ ...prev, types: defTypes, ticketSale: { ...prev.ticketSale, type: defType } }));
            console.log('[TicketSaleCreate] getTypes error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const clear = () => setState(prev => ({
        ...prev,
        ticketSale: {
            ...prev.ticketSale,
            idticket_sale: 0,
            code: 0,
            date: dayjs().format('YYYY-MM-DD'),
            supplier: defSupplier,
            location: defLocation,
            product: defProduct,
            type: defType,
            product_qty: 0,
            qty: 0,
            total_qty: 0,
            price: 0,
            total: 0,
            notes: ''
        }
    }));

    const calculate = async () => {
        showLoader(true);
        const response = await get('/ticketSale/calculate', state.ticketSale);
        if (response.status === 200) {
            const { product_qty, total_qty, price, total } = response.results;
            setState(prev => ({
                ...prev,
                ticketSale: { ...prev.ticketSale, product_qty, total_qty, price, total },
                oldTicketSale: { ...prev.ticketSale, product_qty, total_qty, price, total }
            }));
        } else {
            console.log('[TicketSaleCreate] calculate error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const validateForm = () => {
        const { code, date, supplier, location, product, type, qty } = state.ticketSale;

        if (JSON.stringify(state.ticketSale) !== JSON.stringify(state.oldTicketSale)) {
            showToast('You must calculate before storing', 'error');
            return false;
        }
        if (!code.trim() || !date || !supplier.id || !location.id || !product.id || !type.id || !Number(qty)) {
            showToast('All fields are required', 'error');
            return false;
        }

        store();
    }

    const store = async () => {
        showLoader(true);
        const response = await post('/ticketSale/store', state.ticketSale);
        if (response.status === 200) {
            const { id, message } = response;
            history.push(`/ticket/ticketSale/${id}`);
            showToast(message);
        } else {
            console.log('[TicketSaleCreate] calculate error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    if (state.loading) return <Loader />;
    if (!state.permissions.create_attr) return <NotFound />

    return (
        <div className={classes.root}>
            <h2>{'Create ticket sale'}</h2>
            <Grid container>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Code'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Code'
                                name='code'
                                value={state.ticketSale.code}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Date'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='date'
                                placeholder='Date'
                                name='date'
                                value={state.ticketSale.date}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Supplier'}</FormLabel>
                        <Autocomplete
                            options={state.suppliers}
                            value={state.ticketSale.supplier}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleAutocomplete('supplier', value)}
                            renderInput={params => <TextField {...params} placeholder='Supplier' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Location'}</FormLabel>
                        <Autocomplete
                            options={state.locations}
                            value={state.ticketSale.location}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleAutocomplete('location', value)}
                            renderInput={params => <TextField {...params} placeholder='Location' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Sede'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                type='text'
                                value={sede.name}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <Divider className={classes.divider} />

            <Grid container>
                <Grid item xs={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Product'}</FormLabel>
                        <Autocomplete
                            options={state.products}
                            value={state.ticketSale.product}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => {
                                handleAutocomplete('product', value);
                                (Boolean(value?.id))
                                    ? getTypes(value)
                                    : setState(prev => ({ ...prev, types: defTypes, ticketSale: { ...prev.ticketSale, type: defType, product_qty: 0 } }));
                            }}
                            renderInput={params => <TextField {...params} placeholder='Product' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Type'}</FormLabel>
                        <Autocomplete
                            options={state.types}
                            value={state.ticketSale.type}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => {
                                handleAutocomplete('type', value);
                                setState(prev => ({ ...prev, ticketSale: { ...prev.ticketSale, product_qty: value?.product_type_ref?.qty ?? 0 } }));
                            }}
                            renderInput={params => <TextField {...params} placeholder='Type' />}
                        />
                    </FormGroup>
                </Grid>
            </Grid>

            <Grid container>
                <Grid item xs={6} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Product qty'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                type='number'
                                placeholder='Product qty'
                                value={state.ticketSale.product_qty}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Qty'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='number'
                                inputProps={{ min: 0 }}
                                placeholder='Qty'
                                name='qty'
                                value={state.ticketSale.qty}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Total qty'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                inputProps={{ className: classes.highlight }}
                                type='number'
                                placeholder='Total qty'
                                value={state.ticketSale.total_qty}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Price'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                startAdornment={<InputAdornment position='start'>{'$'}</InputAdornment>}
                                type='number'
                                placeholder='Price'
                                name='price'
                                value={state.ticketSale.price}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Total'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                inputProps={{ className: classes.highlightedNumber }}
                                startAdornment={<InputAdornment position='start'>{'$'}</InputAdornment>}
                                type='number'
                                placeholder='Total'
                                value={state.ticketSale.total}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={6}>
                    <FormGroup>
                        <FormLabel>{'Notes'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Note'
                                name='note'
                                value={state.ticketSale.notes}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>
            <div className={classes.controlsContainer}>
                <Button
                    variant='contained'
                    className={classes.clearBtn}
                    onClick={clear}
                >
                    {'Clear'}
                </Button>
                <Button
                    variant='contained'
                    className={classes.calculateBtn}
                    onClick={calculate}
                >
                    {'Calculate'}
                </Button>
                <Button
                    variant='contained'
                    className={classes.storeBtn}
                    onClick={validateForm}
                >
                    {'Store'}
                </Button>
            </div>
        </div>
    );
}

export default TicketSaleCreate;